import { useCallback, useEffect, useMemo, useRef, useState, type KeyboardEvent } from 'react';
import {
  ANNUAL_DISCOUNT_PERCENT,
  listedPlans,
  publicPlan,
  type PlanTier,
} from '@apicircle-lens/entitlements';
import {
  AccountApiError,
  describeApiError,
  type AccountApi,
  type AccountSummary,
  type CliKeySummary,
  type Device,
  type FirebaseAuthResult,
  type Member,
  type TotpEnrollment,
  type SeatPreview,
} from './api';

// The account app's single view. Deliberately one file and no router: there are
// four sections, they all read from the same summary, and a router would add a
// dependency and a build step for navigation a tab strip already does.
//
// All rules live in `api.ts` and on the server; this only renders them.
//
// Two invariants this file exists to hold, both paid for by real defects:
//
//   1. A BACKGROUND LOAD MUST NEVER CLEAR AN ACTION'S RESULT. `run` and `runLoad`
//      are separate for exactly this reason. The previous build funnelled both
//      through one helper that nulled the notice banner on entry, so creating a
//      CLI key set the notice and then immediately called reload() -> run() ->
//      setNotice(null) in the same commit. The one-time key was destroyed before
//      it ever rendered, for every user, on every browser.
//   2. A VALUE THE SERVER WILL NEVER REISSUE DOES NOT GO IN A BANNER. CLI keys and
//      activation codes get their own state, their own panel, and a dismiss the
//      user controls. See `SecretPanel`.

type Section = 'overview' | 'devices' | 'members' | 'seats' | 'keys';

const SECTIONS: { id: Section; label: string }[] = [
  { id: 'overview', label: 'Overview' },
  { id: 'devices', label: 'Devices' },
  { id: 'members', label: 'Members' },
  { id: 'seats', label: 'Seats' },
  { id: 'keys', label: 'CLI keys' },
];

/** The plan's public name. `plan` is a plain string on the wire, hence the narrowing. */
function planName(plan: string): string {
  return publicPlan(plan as PlanTier)?.name ?? plan;
}

function formatDate(ms: number | null, fallback: string): string {
  return ms === null ? fallback : new Date(ms).toLocaleDateString();
}

/** A device's human label. Never a raw id: an id names the seat, not the machine. */
function deviceLabel(d: Device): string {
  return d.deviceName ?? d.deviceId ?? 'Unnamed device';
}

export function AccountApp({ api }: { api: AccountApi }) {
  const [signedIn, setSignedIn] = useState(api.token() !== null);
  const [section, setSection] = useState<Section>('overview');
  const [summary, setSummary] = useState<AccountSummary | null>(null);
  const [summaryFailed, setSummaryFailed] = useState(false);
  const [checkingSession, setCheckingSession] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  const headingRef = useRef<HTMLHeadingElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const tabRefs = useRef(new Map<Section, HTMLButtonElement | null>());

  /**
   * One failure path. A 401 is an identity event, not a message: the previous
   * build showed "Sign in again" while keeping the user in the signed-in shell,
   * where the only route back was a button labelled "Sign out" — and left the
   * rejected token in storage, so reloading reproduced it.
   */
  const fail = useCallback(
    (e: unknown) => {
      if (e instanceof AccountApiError && (e.status === 401 || e.code === 'unauthorized')) {
        void api.signOut();
        setSignedIn(false);
        setSummary(null);
        setSection('overview');
        setError('Your session has ended. Sign in again.');
        return;
      }
      setError(
        e instanceof AccountApiError
          ? describeApiError(e)
          : 'Could not reach the account service. Check your connection and try again.',
      );
    },
    [api],
  );

  /** A user-initiated action: clears both banners, and marks itself in flight. */
  const run = useCallback(
    async (id: string, fn: () => Promise<void>, options: { preserveBanners?: boolean } = {}) => {
      if (!options.preserveBanners) {
        setError(null);
        setNotice(null);
      }
      setBusy(id);
      try {
        await fn();
      } catch (e) {
        fail(e);
      } finally {
        setBusy(null);
      }
    },
    [fail],
  );

  /**
   * A background load. Reports failure but MUST NOT touch `notice` — see invariant
   * 1 at the top of this file. Every refetch that follows an action goes through here.
   */
  const runLoad = useCallback(
    async (fn: () => Promise<void>) => {
      try {
        await fn();
      } catch (e) {
        fail(e);
      }
    },
    [fail],
  );

  const refresh = useCallback(
    () =>
      runLoad(async () => {
        try {
          setSummary(await api.account());
          setSummaryFailed(false);
        } catch (e) {
          setSummaryFailed(true);
          throw e;
        }
      }),
    [api, runLoad],
  );

  useEffect(() => {
    if (signedIn) void refresh();
  }, [signedIn, refresh]);

  useEffect(() => {
    if (signedIn) {
      setCheckingSession(false);
      return;
    }
    let cancelled = false;
    void api.restoreSession().then((restored) => {
      if (cancelled) return;
      setCheckingSession(false);
      if (restored) setSignedIn(true);
    });
    return () => {
      cancelled = true;
    };
  }, [api, signedIn]);

  // The whole tree is replaced on sign-in and sign-out, which drops focus to
  // <body> with no announcement that anything happened.
  useEffect(() => {
    headingRef.current?.focus();
  }, [signedIn]);

  const signOut = () => {
    void api.signOut();
    setSignedIn(false);
    // Derived state belongs to the identity that fetched it. Leaving it behind
    // showed the previous person's email, plan and seat count to the next one.
    setSummary(null);
    setSummaryFailed(false);
    setSection('overview');
    setError(null);
    setNotice(null);
  };

  // Team-only. On Basic/Pro the pool is a single seat, so a Members tab could
  // only ever say "not available" -- which is noise, not information.
  const tabs = useMemo(
    () => SECTIONS.filter((s) => s.id !== 'members' || summary?.plan === 'team'),
    [summary?.plan],
  );

  // A downgrade can hide the tab the user is standing on.
  useEffect(() => {
    if (!tabs.some((t) => t.id === section)) setSection('overview');
  }, [tabs, section]);

  const selectTab = (id: Section) => {
    setSection(id);
    setError(null);
  };

  const onTabKeys = (e: KeyboardEvent<HTMLDivElement>) => {
    const i = tabs.findIndex((s) => s.id === section);
    const last = tabs.length - 1;
    const next =
      e.key === 'ArrowRight' ? (i + 1) % tabs.length
      : e.key === 'ArrowLeft' ? (i + last) % tabs.length
      : e.key === 'Home' ? 0
      : e.key === 'End' ? last
      : -1;
    if (next < 0) return;
    e.preventDefault();
    const id = tabs[next].id;
    selectTab(id);
    tabRefs.current.get(id)?.focus();
  };

  if (checkingSession) {
    return (
      <main className="page page--narrow">
        <p className="empty">Checking your secure session…</p>
      </main>
    );
  }

  if (!signedIn) {
    return <SignIn api={api} onSignedIn={() => setSignedIn(true)} run={run} busy={busy} error={error} />;
  }

  return (
    <main className="page">
      <div className="appbar">
        <h1 ref={headingRef} tabIndex={-1}>
          API Circle account
        </h1>
        <button type="button" className="btn" onClick={signOut}>
          Sign out
        </button>
      </div>

      <nav aria-label="Account sections">
        <div className="tabs" role="tablist" onKeyDown={onTabKeys}>
          {tabs.map((s) => (
            <button
              key={s.id}
              type="button"
              className="tab"
              id={`tab-${s.id}`}
              role="tab"
              aria-selected={section === s.id}
              aria-controls={`panel-${s.id}`}
              // Roving tabindex: the strip is one tab stop, arrows move within it.
              tabIndex={section === s.id ? 0 : -1}
              ref={(el) => {
                tabRefs.current.set(s.id, el);
              }}
              onClick={() => selectTab(s.id)}
            >
              {s.label}
            </button>
          ))}
        </div>
      </nav>

      {/*
        Mounted unconditionally so assistive tech observes a TEXT CHANGE inside an
        existing region. A region created in the same commit as its content is
        routinely not announced at all.
      */}
      <Banner tone="error" text={error} />
      <Banner tone="ok" text={notice} />

      <div
        className="stack"
        id={`panel-${section}`}
        role="tabpanel"
        aria-labelledby={`tab-${section}`}
        tabIndex={-1}
        ref={panelRef}
      >
        {section === 'overview' && (
          <Overview
            api={api}
            summary={summary}
            failed={summaryFailed}
            run={run}
            busy={busy}
            setNotice={setNotice}
            onChange={refresh}
            onRetry={refresh}
          />
        )}
        {section === 'devices' && <Devices api={api} run={run} runLoad={runLoad} busy={busy} onChange={refresh} />}
        {section === 'members' && (
          <Members api={api} run={run} runLoad={runLoad} busy={busy} summary={summary} />
        )}
        {section === 'seats' && (
          <Seats
            api={api}
            summary={summary}
            failed={summaryFailed}
            run={run}
            busy={busy}
            setNotice={setNotice}
            onChange={refresh}
            onRetry={refresh}
          />
        )}
        {section === 'keys' && <CliKeys api={api} run={run} runLoad={runLoad} busy={busy} setNotice={setNotice} />}
      </div>
    </main>
  );
}

type Run = (id: string, fn: () => Promise<void>, options?: { preserveBanners?: boolean }) => Promise<void>;
type RunLoad = (fn: () => Promise<void>) => Promise<void>;

/** A permanently-mounted live region. Empty renders nothing but keeps the region. */
function Banner({ tone, text }: { tone: 'error' | 'ok'; text: string | null }) {
  return (
    <p
      className={text ? `msg msg--${tone}` : undefined}
      role={tone === 'error' ? 'alert' : 'status'}
      style={text ? undefined : { display: 'none' }}
    >
      {text ?? ''}
    </p>
  );
}

/**
 * A value the server will not reissue. It gets its own panel, is focused and
 * selected on appear so it is one Ctrl+C away, and only an explicit dismiss
 * removes it — never a background refetch.
 */
function SecretPanel({
  title,
  help,
  label,
  value,
  onDismiss,
}: {
  title: string;
  help: string;
  label: string;
  value: string;
  onDismiss: () => void;
}) {
  const ref = useRef<HTMLInputElement>(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    ref.current?.focus();
    ref.current?.select();
  }, []);

  const copy = () => {
    // Clipboard access can be denied or absent; the value stays selected either
    // way, so a failure is not worth an error banner — it just isn't confirmed.
    void navigator.clipboard
      ?.writeText(value)
      .then(() => setCopied(true))
      .catch(() => setCopied(false));
  };

  return (
    <div className="secret">
      <h3>{title}</h3>
      <p className="empty">{help}</p>
      <label htmlFor="secret-value">{label}</label>
      <input id="secret-value" ref={ref} className="secret__value" readOnly value={value} />
      <div className="row">
        <button type="button" className="btn btn--primary" onClick={copy}>
          Copy
        </button>
        <button type="button" className="btn" onClick={onDismiss}>
          I have saved it
        </button>
        <span role="status" className="empty">
          {copied ? 'Copied to the clipboard.' : ''}
        </span>
      </div>
    </div>
  );
}

/** A load that failed, with the retry the previous build never offered. */
function LoadError({ what, onRetry }: { what: string; onRetry: () => void }) {
  return (
    <div className="stack-tight">
      <p className="empty">Could not load {what}.</p>
      <div>
        <button type="button" className="btn" onClick={onRetry}>
          Try again
        </button>
      </div>
    </div>
  );
}

/** A two-step confirm for a destructive row action. No dialog library needed. */
function ConfirmRow({
  question,
  confirmLabel,
  busy,
  onConfirm,
  onCancel,
}: {
  question: string;
  confirmLabel: string;
  busy: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  return (
    <div className="stack-tight" role="group" aria-label={question}>
      <p className="empty">{question}</p>
      <div className="row">
        <button type="button" className="btn btn--danger" disabled={busy} onClick={onConfirm}>
          {confirmLabel}
        </button>
        <button type="button" className="btn btn--quiet" disabled={busy} onClick={onCancel}>
          Cancel
        </button>
      </div>
    </div>
  );
}

function SignIn({
  api,
  onSignedIn,
  run,
  busy,
  error,
}: {
  api: AccountApi;
  onSignedIn: () => void;
  run: Run;
  busy: string | null;
  error: string | null;
}) {
  const [email, setEmail] = useState('');
  const [ssoEmail, setSsoEmail] = useState('');
  const [totpCode, setTotpCode] = useState('');
  const [enrollment, setEnrollment] = useState<TotpEnrollment | null>(null);
  const [authStage, setAuthStage] = useState<'start' | 'email-link-email' | 'mfa' | 'enroll'>('start');
  const [sent, setSent] = useState(false);
  const headingRef = useRef<HTMLHeadingElement>(null);
  const completedInitialAuthRef = useRef(false);

  useEffect(() => {
    headingRef.current?.focus();
  }, []);

  const completeAuthResult = useCallback(
    async (result: FirebaseAuthResult) => {
      if (result.status === 'idle') return;
      if (result.status === 'email_sent') {
        setSent(true);
        setAuthStage('start');
        return;
      }
      if (result.status === 'email_link_email_required') {
        setAuthStage('email-link-email');
        return;
      }
      if (result.status === 'mfa_required') {
        setAuthStage('mfa');
        return;
      }
      if (result.status === 'totp_enrollment_required') {
        setEnrollment(await api.beginTotpEnrollment());
        setAuthStage('enroll');
        return;
      }
      await api.establishFirebaseSession(result.idToken);
      try {
        await api.account();
      } catch (e) {
        void api.signOut();
        throw e;
      }
      onSignedIn();
    },
    [api, onSignedIn],
  );

  useEffect(() => {
    if (!api.firebaseConfigured()) return;
    if (completedInitialAuthRef.current) return;
    completedInitialAuthRef.current = true;
    void run(
      'signin-complete',
      async () => {
        await completeAuthResult(await api.completeFirebaseSignIn(window.location.href));
      },
      { preserveBanners: true },
    );
  }, [api, completeAuthResult, run]);

  const finishMfa = () =>
    void run('signin-mfa', async () => {
      await completeAuthResult(await api.finishTotpSignIn(totpCode));
      setTotpCode('');
    });

  const finishEnrollment = () =>
    void run('signin-enroll', async () => {
      await completeAuthResult(await api.finishTotpEnrollment(totpCode));
      setTotpCode('');
    });

  return (
    <main className="page page--narrow">
      <div className="stack">
        <div>
          <h1 ref={headingRef} tabIndex={-1}>
            Sign in
          </h1>
          <p className="lede">Manage your plan, seats, activated devices and CLI keys.</p>
        </div>

        <Banner tone="error" text={error} />

        {!api.firebaseConfigured() && (
          <p className="msg msg--warn" role="status">
            Firebase login is not configured for this deployment.
          </p>
        )}

        {authStage === 'email-link-email' && (
          <div className="card">
            <h2>Confirm email</h2>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                void run('signin-complete', async () => {
                  await completeAuthResult(await api.completeFirebaseSignIn(window.location.href, email));
                });
              }}
            >
              <div className="stack-tight">
                <div>
                  <label htmlFor="email-link-confirm">Email</label>
                  <input
                    id="email-link-confirm"
                    type="email"
                    autoComplete="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                  />
                </div>
                <div>
                  <button type="submit" className="btn btn--primary" disabled={busy !== null || !api.firebaseConfigured()}>
                    {busy === 'signin-complete' ? 'Checking…' : 'Continue'}
                  </button>
                </div>
              </div>
            </form>
          </div>
        )}

        {authStage === 'mfa' && (
          <div className="card">
            <h2>Authenticator code</h2>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                finishMfa();
              }}
            >
              <div className="stack-tight">
                <div>
                  <label htmlFor="totp-code">Code</label>
                  <input
                    id="totp-code"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    value={totpCode}
                    onChange={(e) => setTotpCode(e.target.value)}
                    required
                  />
                </div>
                <div>
                  <button type="submit" className="btn btn--primary" disabled={busy !== null || totpCode.trim() === ''}>
                    {busy === 'signin-mfa' ? 'Checking…' : 'Continue'}
                  </button>
                </div>
              </div>
            </form>
          </div>
        )}

        {authStage === 'enroll' && enrollment && (
          <div className="card">
            <h2>Set up authenticator</h2>
            <img className="qr" src={enrollment.qrCodeUrl} alt="Authenticator setup QR code" />
            <p className="secret__value">{enrollment.secretKey}</p>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                finishEnrollment();
              }}
            >
              <div className="stack-tight">
                <div>
                  <label htmlFor="enroll-code">Code</label>
                  <input
                    id="enroll-code"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    value={totpCode}
                    onChange={(e) => setTotpCode(e.target.value)}
                    required
                  />
                </div>
                <div>
                  <button type="submit" className="btn btn--primary" disabled={busy !== null || totpCode.trim() === ''}>
                    {busy === 'signin-enroll' ? 'Checking…' : 'Finish setup'}
                  </button>
                </div>
              </div>
            </form>
          </div>
        )}

        {authStage === 'start' && (
          <>
            <div className="card">
              <h2>Email me a link</h2>
              <Banner tone="ok" text={sent ? `Sent to ${email}.` : null} />
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  void run('signin-email', async () => {
                    await api.requestSignInEmail(email);
                    setSent(true);
                  });
                }}
              >
                <div className="stack-tight">
                  <div>
                    <label htmlFor="email">Email</label>
                    <input
                      id="email"
                      type="email"
                      autoComplete="email"
                      required
                      value={email}
                      onChange={(e) => {
                        setEmail(e.target.value);
                        // Changing the address makes the previous confirmation stale.
                        setSent(false);
                      }}
                    />
                  </div>
                  <div>
                    <button type="submit" className="btn btn--primary" disabled={busy !== null || !api.firebaseConfigured()}>
                      {busy === 'signin-email' ? 'Sending…' : sent ? 'Send another link' : 'Email me a sign-in link'}
                    </button>
                  </div>
                </div>
              </form>
            </div>

            <div className="card">
              <h2>Team SSO</h2>
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  void run('signin-sso', () => api.requestSsoSignIn(ssoEmail));
                }}
              >
                <div className="stack-tight">
                  <div>
                    <label htmlFor="sso-email">Work email</label>
                    <input
                      id="sso-email"
                      type="email"
                      autoComplete="email"
                      required
                      value={ssoEmail}
                      onChange={(e) => setSsoEmail(e.target.value)}
                    />
                  </div>
                  <div>
                    <button type="submit" className="btn" disabled={busy !== null || !api.firebaseConfigured()}>
                      {busy === 'signin-sso' ? 'Opening…' : 'Continue with SSO'}
                    </button>
                  </div>
                </div>
              </form>
            </div>
          </>
        )}
      </div>
    </main>
  );
}

function Overview({
  api,
  summary,
  failed,
  run,
  busy,
  setNotice,
  onChange,
  onRetry,
}: {
  api: AccountApi;
  summary: AccountSummary | null;
  failed: boolean;
  run: Run;
  busy: string | null;
  setNotice: (s: string) => void;
  onChange: () => void;
  onRetry: () => void;
}) {
  const [code, setCode] = useState<{ value: string; expiresIn: number } | null>(null);
  const [emailDraft, setEmailDraft] = useState('');

  if (failed) return <LoadError what="your account" onRetry={onRetry} />;
  if (!summary) return <p className="empty">Loading your account…</p>;

  const sub = summary.subscription;
  const unhealthy = sub !== null && sub.status !== 'active';
  const plan = publicPlan(summary.plan as PlanTier);
  const capabilities = plan?.capabilities ?? [];

  return (
    <>
      <section className="card">
        <h2>Plan</h2>
        {unhealthy && (
          <p className="msg msg--warn">
            {sub.status === 'past_due' ?
              `A payment has failed. Your ${planName(summary.plan)} plan ends on ${formatDate(summary.validThrough, 'its renewal date')} unless a payment succeeds.`
            : `This subscription is ${sub.status}. Access ends on ${formatDate(summary.validThrough, 'its renewal date')}.`}
          </p>
        )}
        <dl className="facts">
          <dt>Signed in as</dt>
          <dd>{summary.login}</dd>
          <dt>Plan</dt>
          <dd>{planName(summary.plan)}</dd>
          {sub && (
            <>
              <dt>Status</dt>
              <dd>{sub.status === 'active' ? 'Active' : sub.status}</dd>
              <dt>Billing</dt>
              <dd>{sub.billingPeriod === 'annual' ? 'Annual' : 'Monthly'}</dd>
            </>
          )}
          <dt>{unhealthy ? 'Access ends' : 'Valid through'}</dt>
          <dd>{formatDate(summary.validThrough, 'No expiry')}</dd>
          {sub && (
            <>
              <dt>Seats</dt>
              <dd>
                {sub.seatsInUse} of {sub.seats} in use
              </dd>
            </>
          )}
        </dl>
        <form
          className="row"
          onSubmit={(e) => {
            e.preventDefault();
            const next = emailDraft.trim();
            if (!next) return;
            void run('email-update', async () => {
              await api.requestEmailUpdate(next);
              setEmailDraft('');
              setNotice('Check the new inbox to confirm the email change.');
            });
          }}
        >
          <label className="sr-only" htmlFor="account-email">
            New email address
          </label>
          <input
            id="account-email"
            type="email"
            className="input"
            placeholder="new@example.com"
            value={emailDraft}
            onChange={(e) => setEmailDraft(e.target.value)}
            disabled={busy !== null}
          />
          <button type="submit" className="btn" disabled={busy !== null || emailDraft.trim() === ''}>
            {busy === 'email-update' ? 'Sending…' : 'Change email'}
          </button>
          <button
            type="button"
            className="btn btn--quiet"
            disabled={busy !== null}
            onClick={() =>
              void run('email-sync', async () => {
                if (!(await api.syncFirebaseSession())) return;
                await onChange();
                setNotice('Email refreshed.');
              })
            }
          >
            Refresh email
          </button>
        </form>
        <div className="row">
          {sub ?
            <button
              type="button"
              className="btn"
              disabled={busy !== null}
              onClick={() => void run('billing', () => api.openBillingPortal())}
            >
              {unhealthy ? 'Update payment method' : 'Billing, invoices and cancellation'}
            </button>
          : null}
        </div>
        {!sub && (
          <>
            <p className="empty">You are on the {planName(summary.plan)} plan. No active subscription.</p>
            <PlanPicker api={api} run={run} busy={busy} currentPlan="free" />
          </>
        )}
        {sub && summary.plan !== 'team' && (
          <div className="stack-tight">
            <h3>Upgrade options</h3>
            <PlanPicker api={api} run={run} busy={busy} currentPlan={summary.plan} />
          </div>
        )}
      </section>

      <section className="card">
        <h2>Your plan benefits</h2>
        <p className="empty">
          These are the capabilities currently attached to your API Circle login.
        </p>
        <ul className="list">
          {capabilities.map((cap) => (
            <li key={`${summary.plan}-${cap.label}`} className="list__item">
              <span>{cap.label}</span>
            </li>
          ))}
        </ul>
      </section>

      <section className="card">
        <h2>Activate a device</h2>
        <p className="empty">Generate a code, then enter it in the desktop app to activate it on that machine.</p>
        {code ?
          <SecretPanel
            title="Your activation code"
            help={`Enter this in the desktop app within ${Math.max(1, Math.round(code.expiresIn / 60))} minutes. Generate another if it expires.`}
            label="Activation code"
            value={code.value}
            onDismiss={() => setCode(null)}
          />
        : <div>
            <button
              type="button"
              className="btn"
              disabled={busy !== null}
              onClick={() =>
                void run('activation', async () => {
                  const r = await api.activationCode();
                  setCode({ value: r.code, expiresIn: r.expiresIn });
                })
              }
            >
              {busy === 'activation' ? 'Generating…' : 'Generate activation code'}
            </button>
          </div>
        }
      </section>
    </>
  );
}

/**
 * Who holds a seat, and the controls to change that.
 *
 * A seat IS the membership record, so "add a member" is "give them a seat" and
 * the list is the seat list. Two states matter to the owner and are shown apart:
 * a seat that is waiting (invited, never activated) and a seat in use on a named
 * machine. Without that split, "5 of 5 seats" tells you nothing about whether
 * anybody is actually working.
 */
/**
 * "3 workspaces" / "1 workspace" / "Unlimited workspaces".
 *
 * Exported so the singular case is testable: no LISTED paid plan has exactly
 * one workspace today (Basic 3, Pro 10, Team unlimited), so driving that branch
 * through the rendered grid is impossible without faking the catalog -- which
 * would test the fake. A plan of 1 is a pricing decision away, and "1
 * workspaces" is the kind of thing that ships.
 */
export function workspacesLabel(workspaces: number | 'unlimited'): string {
  if (workspaces === 'unlimited') return 'Unlimited workspaces';
  return `${String(workspaces)} workspace${workspaces === 1 ? '' : 's'}`;
}

/**
 * Choose and buy a plan.
 *
 * Rendered from `listedPlans()` rather than a local list: the catalog is the
 * source both the gate and the marketing site read, and `catalog.test.ts`
 * already pins that every capability it advertises is one the plan actually
 * grants. A hand-written grid here could promise what the gate denies.
 *
 * This replaced a single button hardcoded to `checkout('team','monthly')`,
 * which meant Basic, Pro and annual billing had no purchase path on any
 * surface -- three of four plans unsellable.
 */
function PlanPicker({
  api,
  run,
  busy,
  currentPlan,
}: {
  api: AccountApi;
  run: Run;
  busy: string | null;
  currentPlan?: string;
}) {
  const [period, setPeriod] = useState<'monthly' | 'annual'>('monthly');
  const [seats, setSeats] = useState(2);

  // Free has no price and nothing to charge for; unlisted tiers are internal.
  const plans = useMemo(() => {
    const all = listedPlans();
    const current = all.findIndex((p) => p.tier === currentPlan);
    return all.filter((p, i) => p.price !== null && (current < 0 || i > current));
  }, [currentPlan]);

  if (plans.length === 0) return null;

  return (
    <div className="picker">
      <p className="muted">Your first month is free; annual billing applies the published discount after that.</p>
      <fieldset className="picker__period">
        <legend>Billing period</legend>
        {(['monthly', 'annual'] as const).map((p) => (
          <label key={p} className="picker__periodOption">
            <input
              type="radio"
              name="billing-period"
              value={p}
              checked={period === p}
              disabled={busy !== null}
              onChange={() => setPeriod(p)}
            />
            {p === 'monthly' ? 'Monthly' : `Annual — save ${ANNUAL_DISCOUNT_PERCENT}%`}
          </label>
        ))}
      </fieldset>

      <ul className="picker__plans">
        {plans.map((plan) => {
          const price = plan.price!;
          const perSeat = price.perSeat;
          const headline = period === 'monthly' ? price.monthly : price.annualMonthly;
          return (
            <li key={plan.tier} className="picker__plan">
              <h3>{plan.name}</h3>
              <p className="picker__price">
                <strong>{headline}</strong>
                <span className="muted">
                  {' '}
                  / month{perSeat ? ' per seat' : ''}
                </span>
              </p>
              {period === 'annual' && (
                <p className="muted">
                  Billed {price.annualTotal}
                  {perSeat ? ' per seat' : ''} yearly
                </p>
              )}
              <p className="muted">{plan.blurb}</p>
              <p className="muted">
                {workspacesLabel(plan.workspaces)}
              </p>

              {perSeat && (
                <p>
                  <label htmlFor={`seats-${plan.tier}`}>Seats</label>{' '}
                  <input
                    id={`seats-${plan.tier}`}
                    className="input input--narrow"
                    type="number"
                    min={1}
                    value={seats}
                    disabled={busy !== null}
                    onChange={(e) => setSeats(Math.max(1, Number(e.target.value) || 1))}
                  />
                </p>
              )}

              <button
                type="button"
                className="btn btn--primary"
                disabled={busy !== null}
                onClick={() =>
                  void run(`checkout-${plan.tier}`, () =>
                    // Only a seat-priced plan sends a quantity; the API rejects
                    // one on the others rather than silently multiplying a bill.
                    api.checkout(plan.tier, period, perSeat ? seats : undefined),
                  )
                }
              >
                {busy === `checkout-${plan.tier}` ? 'Opening checkout…' : `Choose ${plan.name}`}
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function Members({
  api,
  run,
  runLoad,
  busy,
  summary,
}: {
  api: AccountApi;
  run: Run;
  runLoad: RunLoad;
  busy: string | null;
  summary: AccountSummary | null;
}) {
  const [members, setMembers] = useState<Member[] | null>(null);
  const [seats, setSeats] = useState<number>(summary?.subscription?.seats ?? 0);
  const [failed, setFailed] = useState(false);
  const [email, setEmail] = useState('');
  const [confirming, setConfirming] = useState<string | null>(null);
  const [added, setAdded] = useState<string | null>(null);

  const load = useCallback(
    () =>
      runLoad(async () => {
        try {
          const res = await api.members();
          setMembers(res.members);
          setSeats(res.seats);
          setFailed(false);
        } catch (e) {
          setFailed(true);
          throw e;
        }
      }),
    [api, runLoad],
  );

  useEffect(() => {
    void load();
  }, [load]);

  const used = members?.length ?? 0;
  const full = members !== null && used >= seats;

  return (
    <section className="card">
      <h2>Members</h2>

      {failed ?
        <LoadError what="your team" onRetry={load} />
      : !members ?
        <p className="empty">Loading your team…</p>
      : <>
          <p className="muted">
            {used} of {seats} {seats === 1 ? 'seat' : 'seats'} in use.
            {full ? ' Add seats to invite anyone else.' : ''}
          </p>

          <form
            className="row"
            onSubmit={(e) => {
              e.preventDefault();
              const value = email.trim();
              if (!value) return;
              void run('add-member', async () => {
                const member = await api.addMember(value);
                setEmail('');
                setAdded(member.email ?? value);
                await load();
              });
            }}
          >
            <label className="sr-only" htmlFor="member-email">
              Teammate's email address
            </label>
            <input
              id="member-email"
              type="email"
              className="input"
              placeholder="teammate@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={full || busy !== null}
            />
            <button type="submit" className="btn" disabled={full || busy !== null || email.trim() === ''}>
              {busy === 'add-member' ? 'Adding…' : 'Add member'}
            </button>
          </form>

          {added !== null && (
            <p className="msg msg--ok" role="status">
              {added} has a seat. They sign in with that address, then activate the desktop app — the seat is
              already waiting for them.
            </p>
          )}

          <ul className="list">
            {members.map((m) => {
              const who = m.email ?? m.accountId;
              return (
                <li key={m.accountId} className="list__item">
                  {confirming === m.accountId ?
                    <ConfirmRow
                      question={`Remove ${who}? Their seat is freed and they are signed out everywhere.`}
                      confirmLabel="Remove"
                      busy={busy !== null}
                      onCancel={() => setConfirming(null)}
                      onConfirm={() =>
                        void run(`remove-${m.accountId}`, async () => {
                          await api.removeMember(m.accountId);
                          setConfirming(null);
                          setAdded(null);
                          await load();
                        })
                      }
                    />
                  : <>
                      <div>
                        <strong>{who}</strong>
                        {m.isOwner && <span className="badge"> Owner</span>}
                        <br />
                        <span className="muted">
                          {m.activated ?
                            `Active on ${m.deviceName ?? 'an unnamed device'}`
                          : 'Seat waiting — not activated yet'}
                        </span>
                      </div>
                      {!m.isOwner && (
                        <button
                          type="button"
                          className="btn btn--quiet"
                          disabled={busy !== null}
                          onClick={() => setConfirming(m.accountId)}
                        >
                          Remove
                        </button>
                      )}
                    </>
                  }
                </li>
              );
            })}
          </ul>
        </>
      }
    </section>
  );
}

function Devices({
  api,
  run,
  runLoad,
  busy,
  onChange,
}: {
  api: AccountApi;
  run: Run;
  runLoad: RunLoad;
  busy: string | null;
  onChange: () => void;
}) {
  const [devices, setDevices] = useState<Device[] | null>(null);
  const [failed, setFailed] = useState(false);
  const [confirming, setConfirming] = useState<string | null>(null);

  const load = useCallback(
    () =>
      runLoad(async () => {
        try {
          setDevices(await api.devices());
          setFailed(false);
        } catch (e) {
          setFailed(true);
          throw e;
        }
      }),
    [api, runLoad],
  );

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <section className="card">
      <h2>Devices</h2>
      {failed ?
        <LoadError what="your devices" onRetry={load} />
      : !devices ?
        <p className="empty">Loading your devices…</p>
      : devices.length === 0 ?
        <p className="empty">No devices are activated yet. Generate an activation code from Overview.</p>
      : <ul className="list">
          {devices.map((d) => {
            const label = deviceLabel(d);
            return (
              <li key={d.seatId} className="list__item">
                {confirming === d.seatId ?
                  <ConfirmRow
                    question={`Deactivate ${label}? That machine loses access until it is activated again with a new code.`}
                    confirmLabel="Deactivate"
                    busy={busy !== null}
                    onCancel={() => setConfirming(null)}
                    onConfirm={() =>
                      void run(`deactivate-${d.seatId}`, async () => {
                        await api.deactivateDevice(d.seatId);
                        setConfirming(null);
                        await load();
                        onChange();
                      })
                    }
                  />
                : <>
                    <span>
                      {label}
                      {d.deviceName === null && <span className="list__meta">seat {d.seatId}</span>}
                    </span>
                    <button
                      type="button"
                      className="btn btn--danger"
                      aria-label={`Deactivate ${label}`}
                      disabled={busy !== null}
                      onClick={() => setConfirming(d.seatId)}
                    >
                      Deactivate
                    </button>
                  </>
                }
              </li>
            );
          })}
        </ul>
      }
    </section>
  );
}

function Seats({
  api,
  summary,
  failed,
  run,
  busy,
  setNotice,
  onChange,
  onRetry,
}: {
  api: AccountApi;
  summary: AccountSummary | null;
  failed: boolean;
  run: Run;
  busy: string | null;
  setNotice: (s: string) => void;
  onChange: () => void;
  onRetry: () => void;
}) {
  // A nullable DRAFT, not a seeded number. Seeding it from an unloaded summary
  // put a value the user never typed into a field wired to a billing call.
  const [draft, setDraft] = useState<string | null>(null);
  const [preview, setPreview] = useState<SeatPreview | null>(null);
  const [confirming, setConfirming] = useState(false);

  if (failed) return <LoadError what="your subscription" onRetry={onRetry} />;
  // Until the summary lands there is nothing true to say about seats. Falling
  // through told paying Team admins that seats belong to a plan they are on.
  if (!summary) return <p className="empty">Loading your subscription…</p>;

  const sub = summary.subscription;
  if (!sub) {
    return (
      <section className="card">
        <h2>Seats</h2>
        <p className="empty">Seats are part of the {planName('team')} plan.</p>
        <PlanPicker api={api} run={run} busy={busy} currentPlan="free" />
      </section>
    );
  }

  const text = draft ?? String(sub.seats);
  const target = Number(text);
  const valid = Number.isInteger(target) && target >= 1 && target <= 999;
  const changed = valid && target !== sub.seats;
  const decreasing = valid && target < sub.seats;
  const locked = decreasing && !sub.canDecreaseSeats;
  const strands = valid && target < sub.seatsInUse;
  const priced = preview !== null && preview.to === target;

  return (
    <section className="card">
      <h2>Seats</h2>
      <p className="empty">
        {sub.seats} seats, {sub.seatsInUse} in use.
      </p>

      <div>
        <label htmlFor="seats">Seats</label>
        <input
          id="seats"
          type="number"
          min={1}
          max={999}
          value={text}
          onChange={(e) => {
            setDraft(e.target.value);
            setPreview(null);
            setConfirming(false);
          }}
        />
      </div>

      {!valid && text.trim() !== '' && <p className="msg msg--warn">Enter a whole number of seats between 1 and 999.</p>}

      {locked && (
        <p className="msg msg--warn" role="status">
          Seats can be reduced once your next renewal has been paid at the current count.
        </p>
      )}

      {strands && !locked && (
        <p className="msg msg--warn" role="status">
          {sub.seatsInUse} devices are active. Reducing to {target} will deactivate {sub.seatsInUse - target} of them at
          your next renewal.
        </p>
      )}

      <div className="row">
        <button
          type="button"
          className="btn"
          disabled={!changed || locked || busy !== null}
          onClick={() =>
            void run('preview', async () => {
              setPreview(await api.previewSeats(target));
            })
          }
        >
          {busy === 'preview' ? 'Pricing…' : 'Preview price'}
        </button>
      </div>

      {priced && (
        <p className="msg msg--ok" role="status">
          {decreasing ?
            `Takes effect at your next renewal. From then: ${preview.currency} ${preview.nextBillingTotal}.`
          : `${preview.currency} ${preview.immediateTotal} today, then ${preview.currency} ${preview.nextBillingTotal} each cycle.`
          }
        </p>
      )}

      {confirming && priced ?
        <ConfirmRow
          question={
            decreasing ?
              `Schedule a drop from ${sub.seats} to ${target} seats at your next renewal?`
            : `Go from ${sub.seats} to ${target} seats and charge ${preview.currency} ${preview.immediateTotal} today?`
          }
          confirmLabel={decreasing ? 'Schedule change' : 'Confirm and pay'}
          busy={busy !== null}
          onCancel={() => setConfirming(false)}
          onConfirm={() =>
            void run('seats', async () => {
              const r = await api.setSeats(target);
              setNotice(
                r.appliedNow ? `Seats updated to ${r.to}.` : `Seats will change to ${r.to} at your next renewal.`,
              );
              setDraft(null);
              setPreview(null);
              setConfirming(false);
              onChange();
            })
          }
        />
      : <div className="row">
          <button
            type="button"
            className="btn btn--primary"
            // A price is a precondition, not an option: nothing commits money
            // until the user has been shown the figure for THIS seat count.
            disabled={!changed || locked || !priced || busy !== null}
            onClick={() => setConfirming(true)}
          >
            {decreasing ? 'Schedule change' : 'Add seats'}
          </button>
          {changed && !priced && !locked && <span className="empty">Preview the price to continue.</span>}
        </div>
      }
    </section>
  );
}

function CliKeys({
  api,
  run,
  runLoad,
  busy,
  setNotice,
}: {
  api: AccountApi;
  run: Run;
  runLoad: RunLoad;
  busy: string | null;
  setNotice: (s: string) => void;
}) {
  const [keys, setKeys] = useState<CliKeySummary[] | null>(null);
  const [failed, setFailed] = useState(false);
  const [name, setName] = useState('');
  const [confirming, setConfirming] = useState<string | null>(null);
  // The created key lives HERE, in state no `run` can reach, and only the user's
  // own dismiss clears it. Routing it through the shared notice channel is what
  // destroyed it before it ever rendered.
  const [issued, setIssued] = useState<{ name: string; key: string } | null>(null);

  const reload = useCallback(
    () =>
      runLoad(async () => {
        try {
          setKeys(await api.cliKeys());
          setFailed(false);
        } catch (e) {
          setFailed(true);
          throw e;
        }
      }),
    [api, runLoad],
  );

  useEffect(() => {
    void reload();
  }, [reload]);

  const live = keys?.filter((k) => k.revokedAt === null) ?? null;

  return (
    <section className="card">
      <h2>CLI keys</h2>

      {issued && (
        <SecretPanel
          title={`Save your key for “${issued.name}”`}
          help="This is the only time it can be shown. If you lose it, revoke this key and create another."
          label="CLI key"
          value={issued.key}
          onDismiss={() => setIssued(null)}
        />
      )}

      <form
        onSubmit={(e) => {
          e.preventDefault();
          void run('create-key', async () => {
            const created = await api.createCliKey(name);
            const keyName = name;
            setName('');
            await reload();
            // Set AFTER the reload, so no later `run` can be interleaved between
            // the value arriving and the user seeing it.
            setIssued({ name: keyName, key: created.key });
          });
        }}
      >
        <div className="stack-tight">
          <div>
            <label htmlFor="keyname">New key name</label>
            <input
              id="keyname"
              required
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Laptop, CI runner…"
            />
          </div>
          <div>
            <button type="submit" className="btn btn--primary" disabled={busy !== null}>
              {busy === 'create-key' ? 'Creating…' : 'Create key'}
            </button>
          </div>
        </div>
      </form>

      {failed ?
        <LoadError what="your CLI keys" onRetry={reload} />
      : live === null ?
        <p className="empty">Loading your CLI keys…</p>
      : live.length === 0 ?
        <p className="empty">No CLI keys yet. Create one to sign in from the command line.</p>
      : <ul className="list">
          {live.map((k) => (
            <li key={k.id} className="list__item">
              {confirming === k.id ?
                <ConfirmRow
                  question={`Revoke ${k.name}? Anything using this key stops working immediately, and this cannot be undone.`}
                  confirmLabel="Revoke key"
                  busy={busy !== null}
                  onCancel={() => setConfirming(null)}
                  onConfirm={() =>
                    void run(`revoke-${k.id}`, async () => {
                      await api.revokeCliKey(k.id);
                      setConfirming(null);
                      setNotice(`Revoked ${k.name}.`);
                      await reload();
                    })
                  }
                />
              : <>
                  <span>
                    {k.name}
                    <span className="list__meta">Last used {formatDate(k.lastUsedAt, 'never')}</span>
                  </span>
                  <button
                    type="button"
                    className="btn btn--danger"
                    aria-label={`Revoke ${k.name}`}
                    disabled={busy !== null}
                    onClick={() => setConfirming(k.id)}
                  >
                    Revoke
                  </button>
                </>
              }
            </li>
          ))}
        </ul>
      }
    </section>
  );
}
